import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UndoIcon from "@mui/icons-material/Undo";
import { SortingState } from "@tanstack/react-table";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import Chip from "components/Chips/Chip";
import DescriptionIcon from "@mui/icons-material/Description";
import LoadingSpinner from "components/LoadingSpinner";
import { app_modules } from "helpers/apps_modules_list";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import usePermission from "hooks/usePermission";
import {
  showCustomSwal,
  showLoading,
  hideLoading,
  showSuccess,
  showError,
} from "helpers/swalHelper";
import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportPurchaseOrderCSV,
  fetchPurchaseOrder,
  revertPurchaseOrder,
  downloadPurchaseOrderPdf,
} from "slices/Procurement/purchase-order/purchase-order/thunk";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import StatusChip from "pages/Enquiry/Components/StatusChip";

type PurchaseOrderProps = {
  handleTabChange?: (tabIndex: number) => void;
  onTableReady?: (table: any) => void;
  noWrapper?: boolean;
};
const PurchaseOrderList: React.FC<PurchaseOrderProps> = () => {
  document.title = "Purchase Log List";
  const dispatch = useDispatch<any>();

  const { canView, canUpdate, canDelete } = usePermission(
    app_modules?.purchase_management?.app_name,
    app_modules?.purchase_management?.modules?.purchase_order
  );

  const selectPO = createSelector(
    (state: any) => state.POreducer,
    (pr) => ({
      POList: pr.POList,
      rowCount: pr.rowCount,
      loading: pr.loading,
      error: pr.error,
    })
  );

  const { POList, rowCount, loading, error } = useSelector(selectPO);
  const [tableInstance, setTableInstance] = useState<any>();

    const tableState = usePreservedTableState({
          key: TableKeys.PURCHASE_MANAGEMENT.PURCHASE_ORDER,
          defaults: {
            pageIndex: 0,
            pageSize: 10,
            search: "",
            searchInput: "",
            sorting: [],
            columnFilters: [],
            filterValue: [],
          },
        });
      
        const {
          pageIndex,
          pageSize,
          search,
          searchInput,
          sorting,
          columnFilters,
          filterValue,
          setPageIndex,
          setPageSize,
          setSearch,
          setSearchInput,
          setSorting,
          setColumnFilters,
          setFilterValue,
          resetState,
        } = tableState

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [revertingId, setRevertingId] = useState<string | null>(null);

  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");

  // Memoize sort values to prevent unnecessary re-renders
  const sortField = useMemo(() => sorting[0]?.id ?? "", [sorting]);
  const sortDirection = useMemo(
    () => (sorting[0]?.desc ? "desc" : "asc"),
    [sorting]
  );

  // Memoize filterValue string to prevent unnecessary re-renders
  const filterValueString = useMemo(
    () => JSON.stringify(filterValue),
    [filterValue]
  );

  const navigate = useNavigate();

  const handleGeneratePI = (id: string, poNumber: string) => {
    // redirect to generate PI form
    console.log(id, poNumber);
    showLoading("Downloading PDF...");
    dispatch(
      downloadPurchaseOrderPdf({
        id: id || "",
        poNumber: poNumber || "",
      })
    )
      .unwrap()
      .then((res: any) => {
         hideLoading();
        if (res) {
          showSuccess("PDF downloaded successfully");
        }
      })
      .catch((err: any) => {
         hideLoading();
        showError(err?.message || "Failed to download PDF");
      });
  };

  // Fetch data when dependencies actually change
  useEffect(() => {
    dispatch(
      fetchPurchaseOrder({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValueString,
  ]);

  useEffect(() => {
    setData(POList);
  }, [POList]);

  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const columns = useMemo(
    () => [
      // {
      //   header: "Sr No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      // },
      {
        header: "PO No.",
        accessorKey: "po_no",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.FILTER_DROPDOWN(
          "po_no"
        )}`,
        cell: ({ row }: { row: any }) => {
          return row.original.po_no || "-";
        },
      },
      {
        header: "PR No.",
        accessorKey: "pr_no__pr_req_no",
        enableSorting: false,
        enableColumnFilter: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.FILTER_DROPDOWN(
          "pr_no__pr_req_no"
        )}`,
        cell: ({ row }: { row: any }) => {
          const prs = row.original?.purchase_order_purchase_requests;
          if (prs.length == 0) return "-";
          return (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
                minWidth: "150px",
              }}
            >
              {prs.map((data: any, index: number) => {
                return <Chip>{data?.purchase_request?.pr_no}</Chip>;
              })}
            </div>
          );
        },
      },
      {
        header: "PO Date",
        accessorKey: "po_date",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.po_date} />
        ),
      },

      {
        header: "Supplier",
        accessorKey: "supplier__supplier_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.FILTER_DROPDOWN(
          "supplier__supplier_name"
        )}`,
        cell: ({ row }: { row: any }) => {
          const supplier = row.original.supplier_name;
          return supplier ? (
            <span style={{ fontWeight: 500 }}>{supplier}</span>
          ) : (
            "-"
          );
        },
      },
      {
        header: "Supplier Branch",
        accessorKey: "supplier_branch__branch_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.FILTER_DROPDOWN(
          "supplier_branch__branch_name"
        )}`,
        cell: ({ row }: { row: any }) => {
          const supplier_branch = row.original.supplier_branch;
          if (!supplier_branch) return "-";

          // Construct full address string
          const addressParts = [
            supplier_branch.address,
            supplier_branch.city,
            supplier_branch.state,
            supplier_branch.country,
            supplier_branch.pincode,
          ].filter(Boolean); // Remove any null/undefined/empty values

          const fullAddress = addressParts.join(", ");

          return (
            <span
              style={{ fontWeight: 500 }}
              title={fullAddress || supplier_branch?.branch_name}
            >
              {supplier_branch?.branch_name}
            </span>
          );
        },
      },
      {
        header: "Initiated By",
        accessorKey: "initiated_by",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.FILTER_DROPDOWN(
          "initiated_by"
        )}`,
        cell: ({ row }: { row: any }) => {
          const initiated_by = row.original.initiated_by;
          return initiated_by ? (
            <span style={{ fontWeight: 500 }}>{initiated_by}</span>
          ) : (
            "-"
          );
        },
      },
      {
        header: "ETA Date",
        accessorKey: "eta_date",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.eta_date} />
        ),
      },
      // {
      //   header: "PO Status",
      //   accessorKey: "status",
      //   enableSorting: true,
      //   enableColumnFilter: true,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.FILTER_DROPDOWN(
      //     "po_status"
      //   )}`,
      //   getFilterValue: (value: string) => value.toLowerCase(),
      //   cell: ({ row }: { row: any }) => {
      //     const status = row.original?.status;
      //     const getStatusColor = (status: string) => {
      //       switch (status?.toLowerCase()) {
      //         case "cancelled":
      //           return "#FF6C6C";
      //         case "inviced":
      //           return "#3F7DDA";
      //         case "short closed":
      //           return "#EAC8B0";
      //         case "open":
      //           return "#FBBD4A";
      //         default:
      //           return "#ccc";
      //       }
      //     };

      //     const formattedStatus =
      //       status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase();

      //     return (
      //       <StatusBadge
      //         status={formattedStatus || "-"}
      //         bgColor={getStatusColor(status)}
      //         color="#fff"
      //       />
      //     );
      //   },
      // },
      {
        header: "Received Status",
        accessorKey: "received_status",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_ORDER?.FILTER_DROPDOWN(
          "received_status"
        )}`,
        cell: ({ row }: { row: any }) => {
          const status = row.original.received_status;
          return <StatusChip status={row.original?.received_status} />;

          // const getStatusColor = (status: string) => {
          //   switch (status?.toLowerCase()) {
          //     case "cancelled":
          //       return "#FF6C6C"; // red
          //     case "partially inwarded":
          //       return "#FDE4B4"; // yellow
          //     case "complete inwarded":
          //       return "#B7FAEB"; // green
          //     case "pending":
          //       return "#E89464"; // orange
          //     default:
          //       return "#FDE4B4"; // fallback gray
          //   }
          // };

          // const displayText = status
          //   ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
          //   : "pending";

          // return (
          //   <StatusBadge
          //     status={displayText}
          //     bgColor={status ? getStatusColor(status) : "#FF6C6C"}
          //     color="#0e0c0c"
          //   />
          // );
        },
      },

            {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },

      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.created_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.created_at}
            />
          );
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.updated_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.updated_at}
            />
          );
        },
      },
      // {
      //   header: "Attachments",
      //   accessorKey: "po_document",
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.po_document ? (
      //       <Link target="_blank" to={row.original.po_document}>
      //         View Attachments
      //       </Link>
      //     ) : (
      //       "No Attachments"
      //     );
      //   },
      // },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const notes = row.original.notes;

          if (!notes) return "-";

          return (
            <Tooltip title={notes} arrow placement="top">
              <Box
                sx={{
                  maxWidth: 250,
                  display: "-webkit-box",
                  WebkitLineClamp: 4, // 👈 change to 3 if needed
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  cursor: "pointer",
                  lineHeight: "1.4em",
                  maxHeight: "5.6em", // 4 lines × lineHeight
                }}
              >
                {notes}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;
          console.log("DATA",data)
          const isPending = data?.received_status?.toLowerCase() === "pending";
          const menuItems: any = [
            {
              label: "View Details",
              icon: VisibilityIcon,
              permission: canView,
              onClick: () =>
                navigate(`/purchase-order-view-details/${data?.po_id}`, {
                  // state: { po_id: data?.po_id },
                }),
              color: "primary",
            },
            ...(isPending
              ? [
                  {
                    label: "Edit",
                    icon: EditIcon,
                    permission: canUpdate,
                    onClick: () =>
                      navigate(`/edit-purchase-order/${data?.po_id}`),
                    color: "primary",
                  },
                  {
                    icon: UndoIcon,
                    label: "Revert",
                    permission: canUpdate,
                    disabled: revertingId === row.original.po_id,
                    onClick: (r: any) => {
                      if (revertingId !== row.original.po_id)
                        handleRevert(r?.po_id);
                    },
                    color: "warning",
                  },
                ]
              : []),
              
            {
              label: "Generate PO",
              icon: <DescriptionIcon fontSize="small" color="warning" />,
              permission: canView,
              onClick: () => handleGeneratePI(data?.po_id, data?.po_no),
            },
            // Revert action
          ];

          return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
        },
      },
    ],
    [POList, revertingId, canView, canUpdate, canDelete, navigate]
  );

  const handleRevert = (po_id: string) => {
    showCustomSwal({
      title: "Are you sure?",
      text: "You want to revert this purchase order!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#f46a6a",
      confirmButtonText: "Yes, revert it!",
      cancelButtonText: "Cancel",
    }).then((result: any) => {
      if (result.isConfirmed) {
        setRevertingId(po_id);
        showLoading("Reverting...");
        dispatch(revertPurchaseOrder(po_id))
          .unwrap()
          .then((res: any) => {
            hideLoading();
            setRevertingId(null);
            showCustomSwal({
              title: "Reverted!",
              text: "Purchase Order has been reverted.",
              icon: "success",
            });
            dispatch(
              fetchPurchaseOrder({
                page: pageIndex + 1,
                pageSize,
                sort: sortField,
                order: sortField ? sortDirection : "",
                search,
                filters: filterValue,
              })
            );
          })
          .catch((error: any) => {
            hideLoading();
            setRevertingId(null);
            console.error("Failed to revert Purchase Order", error);
            showCustomSwal({
              title: "Error",
              text: "Failed to revert Purchase Order",
              icon: "error",
            });
          });
      }
    });
  };

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
              />
            )}
            {canView && (
              <ExportButton
                onExport={async () => {
                  return await exportPurchaseOrderCSV({
                    search,
                    filters: filterValue,
                  });
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <LoaderErrorWrapper loading={loading} error={error}>
            <MasterTableContainer
              columns={columns}
              data={data || []}
              totalRecords={rowCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              colValue={setFilterValue}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              manualSorting={true}
              onTableReady={setTableInstance}
            />
          </LoaderErrorWrapper>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrderList;
